// Third-party imports
import { AuthProvider, HttpError } from 'react-admin';

// Local imports - types
import type { Partner, FilteredPermissionResources, BadgeCredentials, Permissions } from '@src/types';
import { DashboardResources } from '@src/types';

// Local imports - API
import * as RewareApi from '@src/apis/reware';
import getOktaAuth from '@src/services/oktaService';

// Auth error: react-admin will automatically handle certain statuses
class AuthError extends Error {
  public status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

// Constants
const PERMISSIONS_PREFIX = 'config_portal_app_';
const PERMISSION_CODES: FilteredPermissionResources[] = [
  'config_portal_app_plugins',
  'config_portal_app_catalog',
  'config_portal_app_developer_tools',
  'config_portal_app_repair'
];

// Resource permission mapping
const RESOURCE_PERMISSION_MAP: Record<string, string> = {
  [DashboardResources.CatalogItems]: 'config_portal_app_catalog',
  [DashboardResources.VariantSkus]: 'config_portal_app_catalog',
  [DashboardResources.CatalogFieldsMappingUpload]: 'config_portal_app_catalog',
  [DashboardResources.RecyclePlugins]: 'config_portal_app_recycle_plugin',
  [DashboardResources.RepairPlugins]: 'config_portal_app_repair_plugin',
  [DashboardResources.ResalePlugins]: 'config_portal_app_resale_plugin',
  [DashboardResources.TradeinPlugins]: 'config_portal_app_tradein_plugin',
  [DashboardResources.Repairs]: 'config_portal_app_repair',
  'devtools/api-docs': 'config_portal_app_developer_tools',
  [DashboardResources.PricingRules]: 'config_portal_app_admin',
  [DashboardResources.ConditionDiscounts]: 'config_portal_app_admin',
  [DashboardResources.ItemAdjustedPrices]: 'config_portal_app_admin',
  [DashboardResources.ShopifyRedirects]: 'config_portal_app_catalog',
  'shopify-sync': 'config_portal_app_catalog',
};


// Helper functions for session management
const clearSession = () => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('uuid');
  sessionStorage.removeItem('name');
  sessionStorage.removeItem('email');
  sessionStorage.removeItem('partners');
  sessionStorage.removeItem('permissions');
  sessionStorage.removeItem('selectedPartner');
  sessionStorage.removeItem('badgeToken');
};

const storeCredentials = (authResponse: BadgeCredentials) => {
  const { oneTimeToken, uuid } = authResponse ?? {};

  if (!oneTimeToken || !uuid) {
    throw new Error(
      'Authentication failed: missing one or more required credentials (token, UUID).'
    );
  }

  sessionStorage.setItem('token', oneTimeToken);
  sessionStorage.setItem('uuid', uuid);

  // Dispatch custom event for analytics tracking
  window.dispatchEvent(new CustomEvent('userAuthenticated', { detail: { uuid } }));

  if (authResponse.name) {
    sessionStorage.setItem('name', authResponse.name);
  }
  if (authResponse.email) {
    sessionStorage.setItem('email', authResponse.email);
  }

  return oneTimeToken;
};

const authProvider: AuthProvider = {
  // Login method - accepts username and calls badge authentication
  login: async (params: string | { badge: string }) => {
    // Handle both string format and React Admin form object format
    const badgeInput = typeof params === 'string' ? params : params.badge;

    // Strip "usr-" prefix if present, as the backend expects just the badge number
    const username = badgeInput.startsWith('usr-') ? badgeInput.substring(4) : badgeInput;

    try {
      // Step 1: Authenticate and store credentials
      const authResponse = await RewareApi.badgeAuthentication({ username });
      const token = storeCredentials(authResponse);

      // Step 1b: If a JWT was returned (adminAuth), store it for admin_graphql calls
      // and cache the badge token for public API / ps-configs calls
      if (authResponse.token) {
        sessionStorage.setItem('badgeToken', token);
        sessionStorage.setItem('token', authResponse.token);
      }

      // Step 2: Fetch and filter permissions
      const allPermissions = await RewareApi.fetchPermissions({ token });
      if (!allPermissions) {
        throw new AuthError('No user permissions found.', 403);
      }

      const filteredPermissions = Object.fromEntries(
        Object.entries(allPermissions).filter(([key]) =>
          key.startsWith(PERMISSIONS_PREFIX))
      );

      // Check if user has any valid permissions for this application
      if (Object.keys(filteredPermissions).length === 0) {
        throw new Error('No permissions found for this application.');
      }

      sessionStorage.setItem('permissions', JSON.stringify(filteredPermissions));

      // Step 3: Fetch and store partners data
      const partners = await RewareApi.fetchPartners({
        token,
        permissionCodes: PERMISSION_CODES
      });
      let redirectPath = '/select-partner';
      if (partners.length === 1) {
        redirectPath = '/';
        sessionStorage.setItem('selectedPartner', JSON.stringify(partners[0]));
      }
      sessionStorage.setItem('partners', JSON.stringify(partners));

      return { redirectTo: redirectPath };
    } catch (error) {
      console.error('AuthProvider.login failed:', {
        username,
        error: error instanceof Error ? error.message : error,
        stack: error instanceof Error ? error.stack : undefined
      });

      // Preserve specific error information when possible
      if (error instanceof AuthError || error instanceof HttpError) {
        throw error;
      }

      const message = error instanceof Error ? error.message : 'Authentication failed';
      throw new AuthError(message, 401);
    }
  },

  // Logout method - clears session storage and signs out of Okta
  logout: async () => {
    clearSession();
    try {
      const oktaAuth = getOktaAuth();
      const isAuthenticated = await oktaAuth.isAuthenticated();
      if (isAuthenticated) {
        await oktaAuth.signOut({ clearTokensBeforeRedirect: true });
      }
    } catch {
      // Okta signout is best-effort; session is already cleared
    }
  },

  // Check if user is authenticated - validates token in session storage
  checkAuth: async () => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      throw new AuthError('Session invalid or expired.', 401);
    }
    try {
      // Verify the token is still valid by refreshing auth
      const result = await RewareApi.refreshAuthentication({ token });

      if (!result) {
        throw new AuthError('Session invalid or expired.', 401);
      }

    } catch (error) {
      console.error('AuthProvider.checkAuth failed:', {
        tokenPresent: !!token,
        error: error instanceof Error ? error.message : error,
        stack: error instanceof Error ? error.stack : undefined
      });

      if (error instanceof AuthError) throw error;
      if (error instanceof HttpError) throw error;
      const message = (error instanceof Error) ? error.message : 'Authentication request failed.';
      throw new AuthError(`${message}`, 500);
    }
  },

  // Check if an error requires logout (e.g., 401 unauthorized)
  checkError: async (error) => {
    const status = error?.status;
    const message = error?.message || 'Authentication required.';

    if (status === 401) {
      // throwing here automatically logs out, redirects, and displays message
      throw new Error(`${message}`);
    }

    if (status === 403) {
      error.redirectTo = '/access-denied';
      error.logoutUser = false;
      error.message = false;
      throw error;
    }

    // 403 = Forbidden: canAccess method and component handle this case
    // 404, 500, etc - no need to log out
  },

  // Get user identity information
  getIdentity: async () => {
    const uuid = sessionStorage.getItem('uuid');
    const name = sessionStorage.getItem('name');
    const email = sessionStorage.getItem('email');

    if (!uuid) {
      throw new AuthError('User identity not found', 401);
    }

    return {
      id: email || uuid,
      fullName: email || name || uuid, // set to email, layout will display without customization
      email: email || undefined,
      avatar: undefined,
      firstName: name ? name.split(' ')[0] : '',
    };
  },

  // Get user permissions for access control
  getPermissions: async () => {
    const permissionsJson = sessionStorage.getItem('permissions');
    if (!permissionsJson) {
      throw new AuthError('Permissions not found', 403);
    }

    try {
      const permissions = JSON.parse(permissionsJson);
      return permissions;
    } catch (error) {
      throw new AuthError('Failed to parse permissions from session', 500);
    }
  },

  // Check if user can access a specific resource/action
  canAccess: async ({ resource, action }) => {
    // Special case: partner-select should only be shown when multiple partners are available
    if (resource === 'partner-select') {
      const partnersJson = sessionStorage.getItem('partners');
      if (!partnersJson) {
        return false;
      }
      try {
        const partners = JSON.parse(partnersJson);
        return Array.isArray(partners) && partners.length > 1;
      } catch (error) {
        console.error('AuthProvider.canAccess: Failed to parse partners for partner-select', {
          error: error instanceof Error ? error.message : error
        });
        return false;
      }
    }

    const permissionsJson = sessionStorage.getItem('permissions');
    if (!permissionsJson) {
      return false;
    }

    try {
      const permissions = JSON.parse(permissionsJson);

      // Get the actual permission key for this resource
      const permissionKey = RESOURCE_PERMISSION_MAP[resource] || resource;
      const resourcePermissions = permissions[permissionKey as keyof Permissions];
      if (!resourcePermissions) {
        return false;
      }

      // Get currently selected partner and validate it exists in allowed partners
      const selectedPartnerJson = sessionStorage.getItem('selectedPartner');
      const partnersJson = sessionStorage.getItem('partners');

      if (!selectedPartnerJson || !partnersJson) {
        return false;
      }

      let selectedPartnerId: number;
      try {
        const selectedPartner = JSON.parse(selectedPartnerJson);
        const allowedPartners = JSON.parse(partnersJson);
        selectedPartnerId = selectedPartner.id;

        // Validate selected partner is in allowed partners list
        const isPartnerAllowed = allowedPartners.some((partner: Partner) => partner.id === selectedPartnerId);
        if (!isPartnerAllowed) {
          return false;
        }
      } catch (error) {
        console.error('AuthProvider.canAccess: Partner validation failed', {
          resource,
          action,
          error: error instanceof Error ? error.message : error
        });
        return false;
      }

      // Since login only returns partners with 'use' access, we only check 'use' permissions
      // All actions (view and edit) require 'use' permission for the selected partner
      return Array.isArray(resourcePermissions.use) &&
        resourcePermissions.use.includes(selectedPartnerId);
    } catch (error) {
      console.error('AuthProvider.canAccess failed:', {
        resource,
        action,
        error: error instanceof Error ? error.message : error
      });
      return false;
    }
  },
};

export default authProvider;
